"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { motion } from "motion/react"

import { cn } from "@productlane/lib/src/browser"

export interface HoverNavItem {
	key: string
	title: string
	level: number
}

interface Props {
	className?: string
	items: HoverNavItem[]
	onClick?: (key: string) => void
	minHeadlines?: number
	/** Where the headings live, so the nav tracks the right document. */
	headingSelector?: string
	/** The bar that marks the section in view. */
	indicatorClassName?: string
}

const INDENT_PER_LEVEL = ["", "pl-3", "pl-6", "pl-9"] as const

/** How far below the top of the scroll box a heading counts as the one in view. */
const ACTIVE_OFFSET = 96

const BOTTOM_SLACK = 8

/** A heading scrolled onto the line can stop a subpixel short of it. */
const LINE_SLACK = 4

/** The last heading that has passed the line, so scrolling back up walks back. */
export function activeHeadingIndex({
	tops,
	threshold,
	atBottom,
}: {
	/** Each heading's top edge, in viewport coordinates, in document order. */
	tops: number[]
	threshold: number
	atBottom: boolean
}): number {
	if (tops.length === 0) return 0
	// The last heading often sits too low to ever cross the line.
	if (atBottom) return tops.length - 1
	let active = 0
	for (let index = 0; index < tops.length; index++) {
		if ((tops[index] ?? 0) > threshold + LINE_SLACK) break
		active = index
	}
	return active
}

/** The box a heading scrolls inside, falling back to the document. */
function scrollBoxOf(element: HTMLElement): { top: number; atBottom: boolean } {
	let node = element.parentElement
	while (node) {
		const { overflowY } = getComputedStyle(node)
		if (
			(overflowY === "auto" || overflowY === "scroll") &&
			node.scrollHeight > node.clientHeight
		) {
			return {
				top: node.getBoundingClientRect().top,
				atBottom:
					node.scrollTop + node.clientHeight >=
					node.scrollHeight - BOTTOM_SLACK,
			}
		}
		node = node.parentElement
	}
	const doc = document.documentElement
	return {
		top: 0,
		atBottom:
			window.scrollY + window.innerHeight >= doc.scrollHeight - BOTTOM_SLACK,
	}
}

/**
 * The article's own headings, down the right edge, with a bar tracking whatever
 * is on screen. The portal and the docs editor share it so an article reads the
 * same while it is written and after it ships.
 */
export function HoverNav({
	items,
	className,
	onClick,
	minHeadlines = 3,
	headingSelector = ".ProseMirror",
	indicatorClassName = "bg-[var(--portal-accent-color)]",
}: Props) {
	const [activeIndex, setActiveIndex] = useState<number>(0)
	const [indicatorStyle, setIndicatorStyle] = useState({ top: 0, height: 0 })
	const navRef = useRef<HTMLDivElement>(null)
	const itemRefs = useRef<(HTMLAnchorElement | null)[]>([])
	// A clicked item stays active until the reader scrolls on their own, so the
	// smooth scroll it starts (or a heading too low to reach the line) can't override it.
	const pinnedRef = useRef(false)

	const baseLevel = items.length
		? Math.min(...items.map((item) => item.level))
		: 0

	const calculateIndicatorStyle = useCallback(() => {
		const activeItem = itemRefs.current[activeIndex]
		if (!activeItem || !navRef.current) return { top: 0, height: 0 }

		const navRect = navRef.current.getBoundingClientRect()
		const itemRect = activeItem.getBoundingClientRect()

		return {
			top: itemRect.top - navRect.top,
			height: itemRect.height,
		}
	}, [activeIndex])

	useEffect(() => {
		const newStyle = calculateIndicatorStyle()
		if (newStyle.height > 0) {
			setIndicatorStyle(newStyle)
		}
	}, [calculateIndicatorStyle, items])

	useEffect(() => {
		const rafId = requestAnimationFrame(() => {
			const newStyle = calculateIndicatorStyle()
			if (newStyle.height > 0) {
				setIndicatorStyle(newStyle)
			}
		})
		return () => cancelAnimationFrame(rafId)
	}, [calculateIndicatorStyle])

	useEffect(() => {
		if (items.length === 0) return

		let frame = 0

		const measure = () => {
			frame = 0
			if (pinnedRef.current) return
			const root = document.querySelector(headingSelector)
			if (!root) return
			const headings = Array.from(
				root.querySelectorAll<HTMLElement>("h1, h2, h3"),
			).filter((heading) => (heading.textContent ?? "").trim().length > 0)
			const first = headings[0]
			if (!first) return

			const box = scrollBoxOf(first)
			setActiveIndex(
				activeHeadingIndex({
					tops: headings.map((heading) => heading.getBoundingClientRect().top),
					threshold: box.top + ACTIVE_OFFSET,
					atBottom: box.atBottom,
				}),
			)
		}

		const schedule = () => {
			if (frame) return
			frame = requestAnimationFrame(measure)
		}

		const unpin = () => {
			pinnedRef.current = false
		}

		// The editor writes its headings a frame after the content is in.
		schedule()
		// Scroll events never bubble, so capture catches whichever box moves.
		window.addEventListener("scroll", schedule, true)
		window.addEventListener("resize", schedule)
		window.addEventListener("wheel", unpin, { passive: true })
		window.addEventListener("touchstart", unpin, { passive: true })
		window.addEventListener("keydown", unpin)
		window.addEventListener("pointerdown", unpin)

		return () => {
			if (frame) cancelAnimationFrame(frame)
			window.removeEventListener("scroll", schedule, true)
			window.removeEventListener("resize", schedule)
			window.removeEventListener("wheel", unpin)
			window.removeEventListener("touchstart", unpin)
			window.removeEventListener("keydown", unpin)
			window.removeEventListener("pointerdown", unpin)
		}
	}, [items, headingSelector])

	return (
		<nav
			className={cn(
				className,
				"hidden max-h-[calc(100vh-8.1rem)] overflow-y-scroll pb-2 lg:block",
				{
					"!hidden": items.length < minHeadlines,
				},
			)}
		>
			<div ref={navRef} className="relative w-[14rem] pl-4">
				<div className="absolute left-0 top-0 h-full w-[0.5px] bg-gray-200 dark:bg-gray-700" />
				<motion.div
					className={cn(
						"absolute left-0 w-0.5 rounded-full",
						indicatorClassName,
					)}
					initial={false}
					animate={{
						top: indicatorStyle.top,
						height: indicatorStyle.height,
					}}
					transition={{
						type: "spring",
						stiffness: 500,
						damping: 40,
						mass: 0.8,
					}}
				/>
				{items.map(({ key, title, level }, index) => {
					const isActive = activeIndex === index
					const depth = Math.min(
						Math.max(level - baseLevel, 0),
						INDENT_PER_LEVEL.length - 1,
					)
					const indentClass = INDENT_PER_LEVEL[depth]

					return (
						<a
							ref={(el) => {
								itemRefs.current[index] = el
							}}
							href={`#${key}`}
							onClick={(event) => {
								if (onClick) event.preventDefault()
								pinnedRef.current = true
								setActiveIndex(index)
								onClick?.(key)
							}}
							className={cn(
								"relative block py-1.5 text-[0.8125rem] text-gray-500 transition-opacity duration-200 hover:opacity-100 dark:text-gray-400",
								isActive ? "opacity-100" : "opacity-[0.85]",
								indentClass,
							)}
							key={key}
						>
							<div
								className={cn(
									"w-full overflow-hidden text-ellipsis whitespace-nowrap",
									isActive && "text-primary dark:text-gray-100",
								)}
							>
								{title}
							</div>
						</a>
					)
				})}
			</div>
		</nav>
	)
}
